import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getCourses, getCourse, getLessons, getPublicCourses } from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CourseView.css';

const CourseView = ({ isListView = false }) => {
  console.log('CourseView rendering:', { isListView });
  
  const { appId, courseId } = useParams();
  console.log('CourseView params:', { appId, courseId });
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if user has app_editor role
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );
  
  // Extract the application ID if not provided directly - ALWAYS default to 1 for the-faithful-storyteller
  const applicationId = appId || 1; 
  console.log('Using application ID:', applicationId);
  
  useEffect(() => {
    console.log('CourseView useEffect triggered:', { isListView, courseId });
    if (isListView) {
      fetchAllCourses();
    } else {
      fetchCourseAndLessons();
    }
  }, [courseId, isListView]);
  
  const fetchAllCourses = async () => {
    console.log('fetchAllCourses started with applicationId:', applicationId);
    try {
      setLoading(true);
      setError(null);
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      // Use the app-specific endpoint that we confirmed is working
      const numericAppId = applicationId; // This should already be numeric (1)
      const response = await fetch(`/api/apps/${numericAppId}/courses`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin'
        },
        credentials: 'include'
      });
      
      if (response.status === 200) {
        const data = await response.json();
        
        // Handle different possible data structures
        let coursesArray = [];
        if (Array.isArray(data)) {
          coursesArray = data;
        } else if (data.data && Array.isArray(data.data)) {
          coursesArray = data.data;
        } else if (data.courses && Array.isArray(data.courses)) {
          coursesArray = data.courses;
        }
        
        setCourses(coursesArray);
        
        if (coursesArray.length === 0) {
          setError("No courses found. Please check back later for new content.");
        }
      } else {
        setError(`Server returned status ${response.status}. Please try again later.`);
        // Use mock data as fallback
        setCourses(getMockCourses());
      }
    } catch (err) {
      console.error('fetchAllCourses error:', err);
      setError(`Failed to load courses: ${err.message || 'Unknown error'}`);
      // Use mock data as fallback
      setCourses(getMockCourses());
    } finally {
      setLoading(false);
    }
  };
  
  // Mock courses for fallback
  const getMockCourses = () => {
    return [
      {
        id: 1,
        title: "Introduction to the Bible",
        description: "A beginner's guide to understanding the Bible's structure, themes, and message.",
        image_url: "https://via.placeholder.com/300x200?text=Bible+Intro",
        is_published: true
      },
      {
        id: 2,
        title: "The Life of Jesus",
        description: "Explore the life, teachings, and ministry of Jesus Christ through the Gospels.",
        image_url: "https://via.placeholder.com/300x200?text=Jesus",
        is_published: true
      },
      {
        id: 3,
        title: "Old Testament Heroes",
        description: "Learn about the faith journeys of key figures from the Old Testament.",
        image_url: "https://via.placeholder.com/300x200?text=OT+Heroes",
        is_published: true
      }
    ];
  };
  
  const fetchCourseAndLessons = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      // Normalize the app ID to ensure it's a number
      const numericAppId = parseInt(applicationId, 10) || 1;
      const numericCourseId = parseInt(courseId, 10);
      
      // Fetch course details first
      const courseResponse = await fetch(`/api/apps/${numericAppId}/courses/${numericCourseId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin'
        },
        credentials: 'include'
      });
      
      if (courseResponse.status === 200) {
        const courseData = await courseResponse.json();
        
        // Extract course data from various possible response structures
        let courseObject = null;
        if (courseData && !courseData.data && !courseData.course) {
          courseObject = courseData;
        } else if (courseData.data) {
          courseObject = courseData.data;
        } else if (courseData.course) {
          courseObject = courseData.course;
        }
        
        setCourse(courseObject);
        
        // Fetch lessons for this course
        const lessonsResponse = await fetch(`/api/courses/${numericCourseId}/lessons`, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
            'X-App-Domain': domain,
            'X-SaltMarsh-App-Context': 'admin'
          },
          credentials: 'include'
        });
        
        if (lessonsResponse.status === 200) {
          const lessonsData = await lessonsResponse.json();
          
          // Extract lessons data from various possible response structures
          let lessonsArray = [];
          if (Array.isArray(lessonsData)) {
            lessonsArray = lessonsData;
          } else if (lessonsData.data && Array.isArray(lessonsData.data)) {
            lessonsArray = lessonsData.data;
          } else if (lessonsData.lessons && Array.isArray(lessonsData.lessons)) {
            lessonsArray = lessonsData.lessons;
          }
          
          setLessons(lessonsArray);
        } else {
          setLessons([]);
        }
      } else {
        setError("Failed to load course. Please try again later.");
        setCourse(null);
        setLessons([]);
      }
    } catch (err) {
      setError(`Failed to load course: ${err.message || 'Unknown error'}`);
      setCourse(null);
      setLessons([]);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        {!isListView && (
          <Link to="/courses" className="btn btn-primary">
            Back to Courses
          </Link>
        )}
      </div>
    );
  }
  
  if (isListView) {
    return (
      <div className="courses-list container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Available Courses</h1>
          {isAppEditor && (
            <Link to={`/apps/${applicationId}/courses`} className="btn btn-primary">
              <i className="fas fa-cog"></i> Manage Courses
            </Link>
          )}
        </div>

        {loading ? (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : courses.length > 0 ? (
          <div className="row">
            {courses.map(course => (
              <div key={course.id} className="col-md-4 mb-4">
                <div className="card h-100">
                  {course.image_url && (
                    <img 
                      src={course.image_url} 
                      className="card-img-top" 
                      alt={course.title} 
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                  )}
                  <div className="card-body">
                    <h5 className="card-title">{course.title}</h5>
                    <p className="card-text">{course.description}</p>
                  </div>
                  <div className="card-footer bg-transparent border-0">
                    <Link to={`/courses/${course.id}`} className="btn btn-primary">
                      View Course
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="alert alert-info">No courses available at this time. Please check back later.</div>
        )}
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">Course not found</div>
        <Link to="/courses" className="btn btn-primary">
          Back to Courses
        </Link>
      </div>
    );
  }

  return (
    <div className="course-detail container mt-4">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/courses">Courses</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {course.title}
          </li>
        </ol>
      </nav>

      <div className="row mb-4">
        <div className="col-md-8">
          <h1>{course.title}</h1>
          <p className="lead">{course.description}</p>
          
          {isAppEditor && (
            <div className="mb-4">
              <Link 
                to={`/apps/${applicationId}/courses/${course.id}/edit`} 
                className="btn btn-warning me-2"
              >
                <i className="fas fa-edit"></i> Edit Course
              </Link>
              <Link 
                to={`/apps/${applicationId}/courses/${course.id}/lessons`} 
                className="btn btn-info"
              >
                <i className="fas fa-list"></i> Manage Lessons
              </Link>
            </div>
          )}
        </div>
        
        {course.image_url && (
          <div className="col-md-4">
            <img 
              src={course.image_url} 
              alt={course.title} 
              className="img-fluid rounded" 
              style={{ maxHeight: '250px', width: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
      </div>

      <h2 className="mb-3">Lessons</h2>
      {lessons.length > 0 ? (
        <div className="list-group mb-4">
          {lessons.map((lesson, index) => (
            <Link
              key={lesson.id}
              to={`/courses/${course.id}/lessons/${lesson.id}`}
              className="list-group-item list-group-item-action d-flex align-items-center"
            >
              <div className="lesson-number rounded-circle bg-primary text-white me-3">
                {index + 1}
              </div>
              <div>
                <h5 className="mb-1">{lesson.title}</h5>
                <p className="mb-1 text-muted">{lesson.description}</p>
                {lesson.duration && (
                  <small className="text-muted">
                    <i className="fas fa-clock me-1"></i> {lesson.duration} min
                  </small>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="alert alert-info">
          No lessons available for this course yet.
        </div>
      )}

      <Link to="/courses" className="btn btn-secondary">
        <i className="fas fa-arrow-left me-2"></i> Back to All Courses
      </Link>
    </div>
  );
};

export default CourseView; 